<template>
  <div :class="b()">
    <c-modal v-model:open="isVisible">
      <h2>
        {{ $t('c-market-selector.title') }}
      </h2>
      <p :class="b('text')">
        {{ $t('c-market-selector.text') }}
      </p>
      <form
        :class="b('form')"
        @submit.prevent="onSubmit"
      >
        <c-select
          v-model="market"
          :class="b('field', { market: true })"
          :options="mappedMarkets"
          :label="$t('c-market-selector.marketLabel')"
          height="400"
          label-field="country"
          value-field="iso"
          name="market"
          has-search
        />
        <c-select
          v-model="language"
          :class="b('field', { language: true })"
          :label="$t('c-market-selector.languageLabel')"
          :options="languages"
          :disabled="!languages.length"
          height="400"
          label-field="localizedName"
          value-field="iso"
          name="language"
        />
        <div :class="b('actions')">
          <e-button
            :class="b('action')"
            :disabled="!market || !language"
            spacing="600"
            type="submit"
            variant="primary"
          >
            {{ $t('c-market-selector.submitLabel') }}
          </e-button>
        </div>
      </form>
    </c-modal>
  </div>
</template>

<script>
  import useSessionStore from '@/stores/session';
  import cModal from '@/components/c-modal.vue';
  import cSelect from '@/components/c-select.vue';
  import eButton from '@/elements/e-button.vue';

  /**
   * Renders a form to allow the selection of the active market and language.
   */
  export default {
    name: 'c-market-selector',

    components: {
      eButton,
      cModal,
      cSelect,
    },
    // mixins: [],

    // props: {},
    setup() {
      return {
        sessionStore: useSessionStore(),
      };
    },
    data() {
      return {
        /**
         * @type {String} Holds the selected market identifier.
         */
        market: null,

        /**
         * @type {String} Holds the selected language identifier.
         */
        language: null,
      };
    },

    computed: {
      /**
       * Gets/sets the visibility state of the modal.
       */
      isVisible: {
        get() {
          return this.sessionStore.showMarketSelector;
        },
        set(value) {
          this.sessionStore.showMarketSelector = value;
        },
      },

      /**
       * Retrieves the market object for the currently selected market ISO.
       *
       * @returns {Object}
       */
      selectedMarket() {
        const { market } = this;

        return market ? this.sessionStore.markets.find((item) => market === item.iso) : null;
      },

      /**
       * Retrieves the available languages from the selected market.
       *
       * @returns {Array.<Object>}
       */
      languages() {
        return this.selectedMarket?.languages || [];
      },

      /**
       * Maps markets.
       * Combines country name with localized country name
       * if they are not identically.
       *
       * @returns {Array.<Object>}
       */
      mappedMarkets() {
        return (
          this.sessionStore.markets?.map((market) => ({
            ...market,
            country:
              market.country === market.localizedCountry
                ? market.country
                : `${market.localizedCountry} - ${market.country}`,
          })) || []
        );
      },
    },
    watch: {
      selectedMarket(value) {
        this.language = null;

        if (value) {
          this.language = value.languages[0]?.iso;
        }
      },
    },

    // beforeCreate() {},
    // created() {},
    // beforeMount() {},
    // mounted() {},
    // beforeUpdate() {},
    // updated() {},
    // activated() {},
    // deactivated() {},
    // beforeUnmount() {},
    // unmounted() {},

    methods: {
      /**
       * The submit event handler for the market form.
       */
      onSubmit() {
        const redirect = this.selectedMarket?.languages.find((language) => language.iso === this.language)?.url || '/';

        if (
          this.sessionStore.getIsLoggedIn &&
          !this.sessionStore.getUserIsInternal &&
          this.selectedMarket.iso !== this.sessionStore.getUserMarket
        ) {
          this.$cdc.auth.logout(redirect);
        } else {
          window.location.assign(redirect);
        }
      },
    },
    // render() {},
  };
</script>

<style lang="scss">
  @use '@/setup/scss/variables';
  @use '@/setup/scss/mixins';
  @use '@/setup/scss/functions';
  // @use '@/setup/scss/extends' as *;

  .c-market-selector {
    $grid-area--field-1: field-1;
    $grid-area--field-2: field-2;
    $grid-area--actions: actions;

    &__form {
      display: grid;
      grid-gap: functions.rem(variables.$spacing--15) 0;
      grid-template-areas:
        '#{$grid-area--field-1}'
        '#{$grid-area--field-2}'
        '#{$grid-area--actions}';
      grid-template-columns: 1fr;
      grid-template-rows: auto auto auto;
      margin-top: functions.rem(variables.$spacing--30);

      @include mixins.media(sm) {
        display: grid;
        grid-gap: functions.rem(variables.$spacing--15 variables.$spacing--50);
        grid-template-areas:
          '#{$grid-area--field-1} #{$grid-area--actions}'
          '#{$grid-area--field-2} #{$grid-area--actions}';
        grid-template-columns: functions.rem(370px) 1fr;
        grid-template-rows: auto auto;
      }

      @include mixins.media(md) {
        grid-gap: functions.rem(variables.$spacing--25 95px);
        grid-template-areas:
          '#{$grid-area--field-1} #{$grid-area--field-2}'
          '#{$grid-area--actions} #{$grid-area--actions}';
        grid-template-columns: functions.rem(475px) 1fr;
      }
    }

    &__field {
      &--market {
        grid-area: $grid-area--field-1;

        .c-select__output {
          background-image: url('../assets/icon-sprite/i-search.svg');
          background-position: right functions.rem(variables.$spacing--25) center; // Align with focus state.
          background-size: 20px 20px;
        }
      }

      &--language {
        grid-area: $grid-area--field-2;
      }
    }

    &__actions {
      display: flex;
      grid-area: $grid-area--actions;
      align-self: flex-end;
      margin-top: variables.$spacing--35;

      @include mixins.media(sm) {
        justify-content: flex-end;
        margin-top: 0;
      }
    }

    &__text {
      @include mixins.font(variables.$font-size--18);

      @include mixins.media(sm) {
        max-width: functions.rem(410px);
      }

      @include mixins.media(md) {
        max-width: functions.rem(535px);
      }
    }

    &__action {
      @include mixins.media(sm) {
        width: auto;
      }
    }
  }
</style>
